import React, {useEffect, useRef, useState} from 'react';
import {debounce} from 'lodash';
import {CONNECTIVITY_STATUSES} from '@qep/types/src';
import {type Hasi} from '@qep/common';
import Popper from '@mui/material/Popper';
import {getQepSystem} from '@qep/shared/src';
import {NetworkStatusIndicator} from './network-status-indicator.js';
import {StatusClass, StatusIconAssetLocation} from './network-status-types.js';
import './NetworkStatus.css';

type NetworkStatusMenuItem = {
  icon: StatusIconAssetLocation;
  text: string;
};

/* eslint-disable @typescript-eslint/naming-convention */
const displayNames: Record<string, string> = {
  MDR1: 'Streaming Prices 1',
  MDR2: 'Streaming Prices 2',
  MySQL: 'Blotter',
  QEP_DCR: 'Real-Time Orders',
  QEP_OMS: 'Order Manager',
  HTTPCLIENT: 'HTTP Client',
  httpServer: 'HTTP Server',
};
/* eslint-enable @typescript-eslint/naming-convention */

function toDisplayName(componentName: string): string {
  return displayNames[componentName] ?? componentName;
}

function getStatusClass(status: Hasi): StatusClass {
  switch (status.statusInfoString.toLowerCase()) {
    case CONNECTIVITY_STATUSES.OPEN.toLowerCase():
    case CONNECTIVITY_STATUSES.READY.toLowerCase(): {
      return StatusClass.OK;
    }

    case CONNECTIVITY_STATUSES.SLOW.toLowerCase(): {
      return StatusClass.WARNING;
    }

    case CONNECTIVITY_STATUSES.CLOSED.toLowerCase(): {
      return StatusClass.WARNING;
    }

    default: {
      return StatusClass.ERROR;
    }
  }
}

export function NetworkStatusMenu(): React.ReactElement {
  const currentGlobalState = useRef<StatusClass>(StatusClass.WARNING);
  const [menuItems, setMenuItems] = useState<NetworkStatusMenuItem[]>([]);
  const [overallStatus, setOverallStatus] = useState<StatusClass>(StatusClass.OK);
  // eslint-disable-next-line @typescript-eslint/no-restricted-types
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [anchorElement, setAnchorElement] = useState<undefined | HTMLElement>(undefined);
  const open = Boolean(anchorElement);

  function handlePopoverOpen(event: React.MouseEvent<HTMLElement>) {
    setAnchorElement(event.currentTarget);
  }

  useEffect(() => {
    let cancelTimeout: ReturnType<typeof setTimeout> | undefined;

    function checkIfClosePopover(event: MouseEvent) {
      if (!anchorElement) {
        return;
      }

      const inAnchor = Boolean(anchorElement?.contains(event.target as Node));
      const inPopover = Boolean(popoverRef.current?.contains(event.target as Node));

      if (inAnchor || inPopover) {
        clearTimeout(cancelTimeout);
      } else {
        clearTimeout(cancelTimeout);
        cancelTimeout = setTimeout(() => {
          setAnchorElement(undefined);
        }, 90);
      }
    }

    document.addEventListener('mouseenter', checkIfClosePopover, true);

    return () => {
      clearTimeout(cancelTimeout);
      document.removeEventListener('mouseenter', checkIfClosePopover, true);
    };
  }, [anchorElement, setAnchorElement]);

  useEffect(() => {
    const handleStatusesChange = debounce((statuses: Hasi[] = []) => {
      const newMenuItems: NetworkStatusMenuItem[] = [];
      let worstState: StatusClass = StatusClass.OK;

      for (const connectionStatus of statuses) {
        const status = getStatusClass(connectionStatus);

        if (status === StatusClass.WARNING) {
          if (worstState !== StatusClass.ERROR) {
            worstState = StatusClass.WARNING;
          }

          const status = getItemRow(connectionStatus);
          if (status !== undefined) {
            newMenuItems.push(status);
          }
        } else if (status === StatusClass.ERROR) {
          worstState = StatusClass.ERROR;
          const status = getItemRow(connectionStatus);
          if (status !== undefined) {
            newMenuItems.push(status);
          }
        }
      }

      // If switching back to a 'Connected' state, make sure the menu is closed
      // before hiding the menu button
      if (worstState === StatusClass.OK && currentGlobalState.current !== StatusClass.OK) {
        currentGlobalState.current = StatusClass.OK;
      } else if (worstState !== currentGlobalState.current) {
        currentGlobalState.current = worstState;
      }

      setOverallStatus(worstState);
      setMenuItems(newMenuItems);
    }, 500);
    const unsubscribe = getQepSystem().connectivity.listenToStatusesChange(handleStatusesChange);

    return () => {
      unsubscribe();
      handleStatusesChange.cancel();
    };
  }, []);

  const getItemRow = (connectionStatus: Hasi) => {
    switch (connectionStatus.statusInfoString.toLowerCase()) {
      case CONNECTIVITY_STATUSES.CLOSED.toLowerCase(): {
        return {
          icon: StatusIconAssetLocation.ERROR,
          text: `${toDisplayName(connectionStatus.componentName)} connection cannot be established`,
        };
      }

      case CONNECTIVITY_STATUSES.OPEN.toLowerCase():
      case CONNECTIVITY_STATUSES.READY.toLowerCase(): {
        return;
      }

      default: {
        return {
          icon: StatusIconAssetLocation.WARNING,
          text: `${toDisplayName(connectionStatus.componentName)} is in a ${connectionStatus.statusInfoString} state`,
        };
      }
    }
  };

  return (
    <>
      <div
        id='menu-container'
        className='menu-container'
        onMouseEnter={handlePopoverOpen}
      >
        <NetworkStatusIndicator open={open} statusClass={overallStatus} />
      </div>

      <Popper
        id='network-status-menu'
        className='status-menu-main'
        open={open && menuItems.length > 0}
        anchorEl={anchorElement}
      >
        <div className='statuses' ref={popoverRef}>
          <div className={`status-overview ${overallStatus}`}></div>
          <div className='status-list-content'>
            <ul>
              {menuItems.map((item: NetworkStatusMenuItem, i: number) => <li key={i}><img src={item.icon} /><span>{item.text}</span></li>)}
            </ul>
          </div>
        </div>
      </Popper>
    </>
  );
}
